import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  HostListener,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { VideoService } from '../video.service';
import { Router } from '@angular/router';
import { Vehicule } from '../../../model/data-managemnt';
import { DataManagementService } from '../../data-management/data-management.service';
import { SigninService } from '../../../signin/signin.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
})
export class VideoComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('remote_video', { static: false }) videoElementRef: ElementRef<HTMLVideoElement>;
  vehicule: Vehicule;

  isWakingUp = false;
  wakeupTimeRemaining = 0;
  isRecording = false;
  recordingDurationSeconds = 0;

  private wakeupSubscription: Subscription | null = null;
  private wakeupTimeSubscription: Subscription | null = null;
  private isCleaningUp = false;
  private isDestroyed = false;
  private playbackAutoStarted = false;
  private lastStartedSeekToken: string | null = null;
  private mediaRecorder: any = null;
  private recordedChunks: Blob[] = [];
  private recordingTimer: any = null;
  private recordingStartTime = 0;
  private shouldDownloadRecording = false;
  private recordingStream: MediaStream | null = null;

  constructor(
    public videoService: VideoService,
    public signinService: SigninService,
    public dataManagementService: DataManagementService,
    private router: Router,
    private translate: TranslateService,
  ) {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.queryParams) {
      try {
        JSON.parse(navigation.extras.queryParams['type']);
      } catch {
        this.videoService.displayStatus(this.t('VIDEO.error.noVehicleData'), 'error');
      }
    }

    this.vehicule = this.dataManagementService.selectedVehicule;
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }

  get recordingDurationLabel(): string {
    const hours = Math.floor(this.recordingDurationSeconds / 3600);
    const minutes = Math.floor((this.recordingDurationSeconds % 3600) / 60);
    const seconds = this.recordingDurationSeconds % 60;
    return [hours, minutes, seconds].map(v => v.toString().padStart(2, '0')).join(':');
  }

  ngOnInit() {
    this.vehicule = this.dataManagementService.selectedVehicule || this.videoService.vehicule;
    if (!this.vehicule) {
      this.videoService.displayStatus(this.t('VIDEO.error.noVehicle'), 'warning');
      this.router.navigate(['client']);
      return;
    }

    // Live view page defaults to stream mode unless historical playback set it.
    if (this.videoService.type !== 'playback') {
      this.videoService.type = 'stream';
    }

    if (this.videoService.connexion && this.videoService.type === 'stream') {
      this.videoService.teardownVideoSession(this.vehicule);
    }

    this.subscribeToWakeupState();
    this.cancelCountdown();
  }

  ionViewWillEnter() {
    this.isDestroyed = false;
    this.isCleaningUp = false;
    this.vehicule = this.dataManagementService.selectedVehicule || this.videoService.vehicule;
    // Allow a new segment seek to auto-start even if this Ionic page was cached.
    if (this.videoService.type === 'playback') {
      const seek = this.videoService.playbackSeekToken;
      if (seek && seek !== this.lastStartedSeekToken) {
        this.playbackAutoStarted = false;
      }
    } else {
      this.playbackAutoStarted = false;
      this.lastStartedSeekToken = null;
    }
  }

  ionViewDidEnter() {
    this.ensureVideoElementBound();
    this.startPlaybackIfNeeded();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.ensureVideoElementBound();
      this.startPlaybackIfNeeded();
    }, 100);
  }

  ionViewWillLeave() {
    // Keep signaling for return-to-history (`preventMapDestroy` path), but allow
    // a fresh auto-start next time this page is entered with a new seek token.
    this.playbackAutoStarted = false;
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.wakeupSubscription?.unsubscribe();
    this.wakeupTimeSubscription?.unsubscribe();
    this.stopRecordingIfNeeded(false);
    this.performEmergencyCleanup();
  }

  private subscribeToWakeupState(): void {
    this.wakeupSubscription = this.videoService.isWakingUp$.subscribe((waking) => {
      this.isWakingUp = waking;
    });
    this.wakeupTimeSubscription = this.videoService.wakeupTimeRemaining$.subscribe((remaining) => {
      this.wakeupTimeRemaining = remaining;
    });
  }

  private ensureVideoElementBound(): boolean {
    if (this.isDestroyed || !this.videoElementRef?.nativeElement) {
      return false;
    }
    const success = this.videoService.setVideoElement(this.videoElementRef);
    if (success) {
      const video = this.videoElementRef.nativeElement;
      // Keep muted — unmuting without a gesture often yields a black WebView frame.
      video.muted = true;
      video.playsInline = true;
      if ((this.videoService.stream || this.videoService.playback) && video.srcObject) {
        video.play().catch(() => undefined);
      }
      this.initializeVideoStream();
    }
    return success;
  }

  private initializeVideoStream(): void {
    if (this.videoService.type === 'stream' && this.vehicule && !this.videoService.connexion) {
      this.videoService.onConnectClick(this.vehicule);
    }
  }

  private startPlaybackIfNeeded(): void {
    if (this.isDestroyed) {
      return;
    }
    if (this.videoService.type !== 'playback' || !this.vehicule) {
      return;
    }

    const seekToken = this.videoService.playbackSeekToken
      || (this.videoService.path?.displayBeginPathTime
        ? this.videoService.formatPathPlaybackDate(this.videoService.path)
        : '');

    // Already requested for this seek (segment click may have sent it before navigate).
    if (this.videoService.isHistoricalPlaybackPendingOrActive()) {
      this.playbackAutoStarted = true;
      this.lastStartedSeekToken = seekToken || this.lastStartedSeekToken;
      return;
    }

    if (this.playbackAutoStarted && this.lastStartedSeekToken && this.lastStartedSeekToken === seekToken) {
      return;
    }

    this.playbackAutoStarted = true;
    this.lastStartedSeekToken = seekToken || null;
    const channel = this.signinService.hasVehicleFrontCameraOption(this.vehicule) ? 'CH1' : 'CH2';
    void this.videoService.startHistoricalPlayback(channel)
      .catch((err) => {
        console.error('Camera page historical playback failed', err);
        this.playbackAutoStarted = false;
      });
  }

  selectCamera(kind: 'driver' | 'front' | 'rear'): void {
    if (this.isCleaningUp || this.isDestroyed) {
      return;
    }

    if (!this.videoService.isVideoElementReady()) {
      if (!this.ensureVideoElementBound()) {
        this.videoService.displayStatus(this.t('VIDEO.status.initializing'), 'info');
        return;
      }
    }

    if (!this.videoService.connexion) {
      this.videoService.displayStatus(this.t('VIDEO.connection.notConnectedToVehicle'), 'warning');
      this.videoService.onConnectClick(this.vehicule);
      return;
    }

    if (this.isWakingUp) {
      this.videoService.displayStatus(this.t('VIDEO.wakeup.inProgress'), 'info');
      return;
    }

    // User gesture: try to unlock unmuted playback on mobile WebViews.
    const video = this.videoElementRef?.nativeElement;
    if (video) {
      video.muted = false;
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => undefined);
      });
    }

    const cameraMap = {
      driver: { stream: 'S5', playback: 'CH2' },
      front: { stream: 'S4', playback: 'CH1' },
      rear: { stream: 'S6', playback: 'CH3' },
    };
    const params = cameraMap[kind];
    this.videoService.streamOrPlayback(params.stream, params.playback);
  }

  captureImage(): void {
    const videoElement = this.videoElementRef?.nativeElement;
    if (!videoElement) {
      this.videoService.displayStatus(this.t('VIDEO.error.videoSetupFailed'), 'error');
      return;
    }

    if (!videoElement.videoWidth || !videoElement.videoHeight) {
      this.videoService.displayStatus(this.t('VIDEO.error.noVideoStream'), 'warning');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      this.videoService.displayStatus(this.t('VIDEO.error.unknownError'), 'error');
      return;
    }

    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        this.videoService.displayStatus(this.t('VIDEO.error.unknownError'), 'error');
        return;
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const vehicleLabel = this.vehicule?.matricule || 'vehicle';
      const fileName = `capture_${vehicleLabel}_${timestamp}.png`;

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      this.videoService.displayStatus(this.t('VIDEO.controls.imageCaptured'), 'success');
    }, 'image/png');
  }

  toggleRecording(): void {
    if (this.isRecording) {
      this.stopRecordingIfNeeded(true);
      return;
    }
    this.startRecording();
  }

  private startRecording(): void {
    const videoElement = this.videoElementRef?.nativeElement;
    if (!videoElement) {
      this.videoService.displayStatus(this.t('VIDEO.error.videoSetupFailed'), 'error');
      return;
    }

    const srcObject = videoElement.srcObject as MediaStream | null;
    const capturedStream =
      typeof (videoElement as any).captureStream === 'function'
        ? (videoElement as any).captureStream()
        : typeof (videoElement as any).mozCaptureStream === 'function'
          ? (videoElement as any).mozCaptureStream()
          : null;

    this.recordingStream =
      capturedStream && capturedStream.getTracks?.().length > 0
        ? capturedStream
        : srcObject;

    if (!this.recordingStream || this.recordingStream.getTracks().length === 0) {
      this.videoService.displayStatus(this.t('VIDEO.error.noVideoStream'), 'warning');
      return;
    }

    const mimeType = this.getSupportedRecordingMimeType();
    try {
      this.recordedChunks = [];
      const RecorderCtor = (window as any).MediaRecorder;
      if (!RecorderCtor) {
        this.videoService.displayStatus(this.t('VIDEO.error.unknownError'), 'error');
        return;
      }
      this.mediaRecorder = mimeType
        ? new RecorderCtor(this.recordingStream, { mimeType })
        : new RecorderCtor(this.recordingStream);

      this.mediaRecorder.ondataavailable = (event: any) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        if (this.shouldDownloadRecording) {
          setTimeout(() => this.downloadRecordedVideo(), 250);
        } else {
          this.recordedChunks = [];
        }
        this.shouldDownloadRecording = false;
        this.recordingStream = null;
      };

      this.mediaRecorder.onerror = () => {
        this.videoService.displayStatus(this.t('VIDEO.error.unknownError'), 'error');
      };

      this.mediaRecorder.start(1000);
      this.isRecording = true;
      this.recordingDurationSeconds = 0;
      this.recordingStartTime = Date.now();
      this.startRecordingTimer();
      this.videoService.displayStatus(this.t('VIDEO.controls.recordingStarted'), 'success');
    } catch (error) {
      console.error('Could not start recording:', error);
      this.videoService.displayStatus(this.t('VIDEO.error.unknownError'), 'error');
    }
  }

  private stopRecordingIfNeeded(download: boolean): void {
    if (!this.mediaRecorder) {
      this.isRecording = false;
      this.stopRecordingTimer();
      this.recordingStream = null;
      return;
    }

    const recorder = this.mediaRecorder;
    this.mediaRecorder = null;
    this.isRecording = false;
    this.stopRecordingTimer();
    this.shouldDownloadRecording = download;

    if (!download) {
      recorder.onstop = null;
      this.recordedChunks = [];
      this.shouldDownloadRecording = false;
      this.recordingStream = null;
    }

    if (recorder.state !== 'inactive') {
      if (download) {
        try {
          recorder.requestData();
        } catch {
          // Ignore when requestData is not available.
        }
      }
      recorder.stop();
    } else if (download) {
      this.downloadRecordedVideo();
      this.shouldDownloadRecording = false;
      this.recordingStream = null;
    }
  }

  private downloadRecordedVideo(): void {
    if (!this.recordedChunks.length) {
      return;
    }

    const videoBlob = new Blob(this.recordedChunks, {
      type: this.recordedChunks[0].type || 'video/webm',
    });
    this.recordedChunks = [];
    const extension = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const vehicleLabel = this.vehicule?.matricule || 'vehicle';
    const fileName = `record_${vehicleLabel}_${timestamp}.${extension}`;

    const blobUrl = URL.createObjectURL(videoBlob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);

    this.videoService.displayStatus(this.t('VIDEO.controls.recordingSaved'), 'success');
  }

  private getSupportedRecordingMimeType(): string {
    const candidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ];
    for (const mimeType of candidates) {
      if ((window as any).MediaRecorder?.isTypeSupported?.(mimeType)) {
        return mimeType;
      }
    }
    return '';
  }

  private startRecordingTimer(): void {
    this.stopRecordingTimer();
    this.recordingTimer = setInterval(() => {
      this.recordingDurationSeconds = Math.floor((Date.now() - this.recordingStartTime) / 1000);
    }, 1000);
  }

  private stopRecordingTimer(): void {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    this.recordingDurationSeconds = 0;
  }

  handleHangup() {
    try {
      this.stopRecordingIfNeeded(true);
      this.videoService.hangUpCall(this.vehicule);
    } catch (error) {
      console.error('Error during hangup:', error);
    }
  }

  cancelCountdown() {
    this.videoService.counterLoading = false;
    this.videoService.counter = 0;
  }

  cleanup() {
    this.stopRecordingIfNeeded(true);
    this.videoService.teardownVideoSession(this.vehicule);
    this.videoService.resetService();
  }

  close() {
    this.performEmergencyCleanup();
    this.router.navigate([this.videoService.returnRoute || 'client']);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.performEmergencyCleanup();
    return true;
  }

  @HostListener('window:visibilitychange')
  visibilityChangeHandler() {
    if (document.visibilityState === 'hidden' && this.videoService.type !== 'playback') {
      this.performEmergencyCleanup();
    }
  }

  @HostListener('window:pagehide')
  pageHideHandler() {
    if (this.videoService.type !== 'playback') {
      this.performEmergencyCleanup();
    }
  }

  @HostListener('window:freeze')
  pageFreezeHandler() {
    if (this.videoService.type !== 'playback') {
      this.performEmergencyCleanup();
    }
  }

  private performEmergencyCleanup() {
    if (this.isCleaningUp) {
      return;
    }
    this.isCleaningUp = true;
    try {
      this.stopRecordingIfNeeded(false);
      this.videoService.teardownVideoSession(this.vehicule);
    } catch (e) {
      console.error('Error during emergency cleanup', e);
    }
  }
}
